(() => {
  const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ß/g, 'ss').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const initialized = new WeakSet();

  function setup(form) {
    if (initialized.has(form)) return;
    initialized.add(form);
    const input = form.querySelector('input[name="q"]');
    const dropdown = form.querySelector('[data-fw-search-dropdown]');
    const list = form.querySelector('[role="listbox"]');
    const allLink = form.querySelector('[data-fw-search-all]');
    const status = form.querySelector('[data-fw-search-status]');
    const allLabel = allLink.textContent;
    let catalog;
    try { catalog = JSON.parse(form.querySelector('[data-fw-search-catalog]').textContent); } catch { return; }
    const money = new Intl.NumberFormat(form.dataset.locale || 'de', { style: 'currency', currency: form.dataset.currency || 'EUR' });
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    const urlFor = (path) => {
      const url = new URL(path, location.origin);
      if (url.origin !== location.origin) return form.action;
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url.href;
    };
    if (preview) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden'; hidden.name = 'preview_theme_id'; hidden.value = preview;
      form.append(hidden);
    }
    let timer;
    let controller;
    let revision = 0;
    let active = -1;
    let composing = false;
    const listeners = new AbortController();
    const on = (target, event, handler) => target.addEventListener(event, handler, { signal: listeners.signal });
    const close = () => {
      dropdown.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
      list.querySelectorAll('[role="option"]').forEach((item) => item.setAttribute('aria-selected', 'false'));
    };
    const cancel = () => { clearTimeout(timer); controller?.abort(); revision += 1; };
    const updateAllLink = (query) => {
      const url = new URL(form.action);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      allLink.href = urlFor(url);
      allLink.textContent = `${allLabel}: „${query}“`;
    };
    const render = (products, query) => {
      const selectedHref = list.querySelector('[aria-selected="true"] a')?.href;
      list.replaceChildren();
      products.slice(0, 6).forEach((product, index) => {
        const item = document.createElement('li');
        item.id = `${list.id}-${index}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        const link = document.createElement('a');
        link.href = urlFor(product.url);
        link.className = 'fw-search-product';
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = ''; image.width = 64; image.height = 64;
          link.append(image);
        }
        const content = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = product.title;
        const price = document.createElement('span');
        const hasPrice = Number.isFinite(product.price) && product.price > 0;
        price.textContent = hasPrice ? money.format(product.price / 100) : '';
        if (product.pack) price.textContent += `${hasPrice ? ' · ' : ''}${product.pack}`;
        content.append(title, price);
        if (hasPrice && typeof product.unitPrice === 'string' && product.unitPrice.trim()) {
          const unitPrice = document.createElement('span');
          unitPrice.className = 'fw-search-unit-price';
          unitPrice.textContent = product.unitPrice.trim();
          content.append(unitPrice);
        }
        if (product.local) {
          const badge = document.createElement('small');
          badge.textContent = form.dataset.preorderLabel;
          content.append(badge);
        }
        link.append(content); item.append(link); list.append(item);
      });
      active = -1;
      input.removeAttribute('aria-activedescendant');
      if (selectedHref) {
        const items = [...list.children];
        active = items.findIndex((item) => item.querySelector('a').href === selectedHref);
        if (active >= 0) {
          items[active].setAttribute('aria-selected', 'true');
          input.setAttribute('aria-activedescendant', items[active].id);
        }
      }
      updateAllLink(query);
      dropdown.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      status.textContent = `${form.dataset.resultsLabel}: ${Math.min(products.length, 6)}`;
    };
    const search = () => {
      cancel();
      if (composing) return;
      const query = input.value.trim();
      const normalized = normalize(query);
      if (normalized.length < 2) { close(); status.textContent = ''; return; }
      const words = normalized.split(' ');
      const local = catalog.filter((product) => words.every((word) => normalize(`${product.title} ${product.handle}`).includes(word))).map((product) => ({ ...product, local: true }));
      render(local, query);
      const requestRevision = revision;
      timer = setTimeout(async () => {
        controller = new AbortController();
        const url = new URL(form.dataset.predictiveUrl, location.origin);
        url.searchParams.set('section_id', 'fw-search-products');
        url.searchParams.set('_fw_search', Date.now().toString());
        if (preview) url.searchParams.set('preview_theme_id', preview);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
        try {
          const response = await fetch(url, { signal: controller.signal, cache: 'no-store', headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Search unavailable');
          const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
          const data = JSON.parse(markup.querySelector('[data-fw-search-products]').textContent);
          if (revision !== requestRevision || !form.contains(document.activeElement)) return;
          const products = [...local];
          const ids = new Set(local.filter((product) => product.id).map((product) => String(product.id)));
          const handles = new Set(local.map((product) => product.handle));
          for (const product of data) {
            if (local.length && !words.every((word) => normalize(`${product.title} ${product.handle} ${product.type || ''}`).includes(word))) continue;
            if (ids.has(String(product.id)) || handles.has(product.handle)) continue;
            products.push({ ...product, price: product.price === null ? NaN : Number(product.price) });
            ids.add(String(product.id)); handles.add(product.handle);
          }
          render(products, query);
        } catch (error) {
          if (error.name !== 'AbortError' && revision === requestRevision && form.contains(document.activeElement)) render(local, query);
        }
      }, 180);
    };
    on(input, 'input', search);
    on(input, 'focus', search);
    on(input, 'compositionstart', () => { composing = true; cancel(); close(); });
    on(input, 'compositionend', () => { composing = false; search(); });
    on(input, 'keydown', (event) => {
      if (event.key === 'Escape') { cancel(); close(); return; }
      if (dropdown.hidden || composing) return;
      const items = [...list.children];
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && items.length) {
        event.preventDefault();
        active = event.key === 'ArrowDown' ? (active + 1) % items.length : (active < 0 ? items.length - 1 : (active - 1 + items.length) % items.length);
        items.forEach((item, index) => item.setAttribute('aria-selected', String(index === active)));
        input.setAttribute('aria-activedescendant', items[active].id);
        items[active].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && active >= 0) {
        event.preventDefault();
        location.assign(items[active].querySelector('a').href);
      }
    });
    on(form, 'submit', () => { cancel(); close(); });
    on(form, 'reset', () => { cancel(); close(); status.textContent = ''; });
    // Keep a touched or focused result stable until its native link is activated.
    on(dropdown, 'pointerdown', cancel);
    on(dropdown, 'focusin', cancel);
    on(form, 'focusout', (event) => {
      if (form.contains(event.relatedTarget)) return;
      cancel();
      // Safari can blur the input with no relatedTarget before a link's click.
      // Dismiss only on a known outside focus target or an outside interaction.
      if (event.relatedTarget) close();
    });
    on(document, 'focusin', (event) => { if (!form.contains(event.target)) { cancel(); close(); } });
    on(document, 'pointerdown', (event) => { if (!form.contains(event.target)) { cancel(); close(); } });
    on(document, 'shopify:section:unload', (event) => {
      if (event.target.contains(form)) { cancel(); listeners.abort(); initialized.delete(form); }
    });
  }
  const init = () => document.querySelectorAll('[data-fw-search]').forEach(setup);
  init();
  document.addEventListener('shopify:section:load', init);
})();
